/*
 *   Copyright 2022-2023  SenX S.A.S.
 *
 *   Licensed under the Apache License, Version 2.0 (the "License");
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars,max-classes-per-file
import { h } from "@stencil/core";
import { Logger } from "../../../utils/logger";
import { Param } from "../../../model/param";
import { GTSLib } from "../../../utils/gts.lib";
import { Utils } from "../../../utils/utils";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import duration from "dayjs/plugin/duration";
dayjs.extend(duration);
dayjs.extend(relativeTime);
class Cell {
}
export class DiscoveryPageable {
    constructor() {
        this.page = 0;
        this.pages = [];
        this.displayedValues = [];
        this.sortAsc = true;
        this.filters = {};
        this.sortCol = -1;
        this.debug = false;
        this.divider = undefined;
        this.data = undefined;
        this.options = new Param();
        this.params = [];
        this.elemsCount = 15;
        this.windowed = 5;
    }
    updateData() {
        this.drawGridData();
    }
    componentWillLoad() {
        this.LOG = new Logger(DiscoveryPageable, this.debug);
        this.drawGridData();
    }
    goto(page) {
        this.page = page;
        this.drawGridData();
    }
    next() {
        this.page = Math.min(this.page + 1, this.data.values.length - 1);
        this.drawGridData();
    }
    prev() {
        this.page = Math.max(this.page - 1, 0);
        this.drawGridData();
    }
    drawGridData() {
        var _a, _b;
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['drawGridData', '_options'], this.options);
        if (!this.data) {
            return;
        }
        const options = Utils.mergeDeep(Object.assign(Object.assign({}, new Param()), { timeMode: 'date' }), this.options || {});
        this.options = Object.assign({}, options);
        this.pages = [];
        for (let i = 0; i < (this.data.values || []).length / this.elemsCount; i++) {
            this.pages.push(i);
        }
        this.elemsCount = this.options.elemsCount || this.elemsCount;
        this.windowed = this.options.windowed || this.windowed;
        const dataset = (this.data.values || [])
            .map(row => row.map((v, i) => i === 0 ? this.formatDate(v) : this.formatValue(v)))
            .filter(d => {
            let found = Object.keys(this.filters).length === 0;
            Object.keys(this.filters).forEach(k => found = found || d[k].display.toLowerCase().startsWith(this.filters[k].toLowerCase()));
            return found;
        });
        if (this.sortCol >= 0) {
            dataset.sort((a, b) => {
                switch (a[this.sortCol].type) {
                    case 'string':
                        return this.sortAsc
                            ? a[this.sortCol].display.localeCompare(b[this.sortCol].display)
                            : b[this.sortCol].display.localeCompare(a[this.sortCol].display);
                    default:
                        return this.sortAsc
                            ? a[this.sortCol].value - b[this.sortCol].value
                            : b[this.sortCol].value - a[this.sortCol].value;
                }
            });
        }
        this.displayedValues = dataset.slice(Math.max(0, this.elemsCount * this.page), Math.min(this.elemsCount * (this.page + 1), (this.data.values || []).length));
        (_b = this.LOG) === null || _b === void 0 ? void 0 : _b.debug(['drawGridData', 'data'], this.data, {
            windowed: this.windowed,
            elemsCount: this.elemsCount,
            displayedValues: this.displayedValues,
        });
    }
    static formatLabel(name) {
        return GTSLib.formatLabel(name);
    }
    setSelected(value) {
        this.dataPointSelected.emit({
            date: this.data.isGTS ? value[0].value : undefined,
            name: this.data.name,
            value: value,
            meta: { header: this.data.headers }
        });
    }
    setOver(value) {
        this.dataPointOver.emit({
            date: this.data.isGTS ? value[0].value : undefined,
            name: this.data.name,
            value: value,
            meta: { header: this.data.headers }
        });
    }
    sort(header) {
        var _a, _b;
        if ((_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.tabular) === null || _b === void 0 ? void 0 : _b.sortable) {
            this.sortAsc = this.sortCol !== header ? true : !this.sortAsc;
            this.sortCol = header;
            this.drawGridData();
        }
    }
    filter(i, e) {
        if (e.srcElement.value !== '') {
            this.filters[i] = e.srcElement.value;
        }
        else {
            delete this.filters[i];
        }
        this.drawGridData();
    }
    formatDate(v) {
        if (this.data.isGTS) {
            return this.formatValue({ value: v, type: 'date' });
        }
        else {
            return this.formatValue(v);
        }
    }
    formatValue(v) {
        if (typeof v !== 'object') {
            v = {
                value: v !== undefined ? v : '',
                type: typeof v,
                unit: '',
                display: v.toString() || ''
            };
        }
        else {
            v = Object.assign({ type: 'string', unit: '' }, v);
        }
        let res = v.value !== undefined ? v.value : '';
        switch (v.type) {
            case 'elapsed':
                if (res !== undefined && !isNaN(parseInt(res, 10))) {
                    res = dayjs().to(dayjs(GTSLib.toISOString(parseInt(res, 10), this.divider, this.options.timeZone, this.options.fullDateDisplay ? this.options.timeFormat : undefined)));
                }
                else {
                    res = '';
                }
                break;
            case 'date':
                if (res !== undefined && !isNaN(parseInt(res, 10))) {
                    const format = v.format || (this.options.fullDateDisplay ? this.options.timeFormat : undefined);
                    res = GTSLib.toISOString(parseInt(res, 10), this.divider, this.options.timeZone, format)
                        .replace('T', ' ')
                        .replace(/\+[0-9]{2}:[0-9]{2}$/gi, '');
                }
                else {
                    res = '';
                }
                break;
            case 'duration':
                const format = v.format || (this.options.fullDateDisplay ? this.options.timeFormat : undefined);
                if (res !== undefined && !isNaN(parseInt(res, 10))) {
                    res = dayjs.duration(parseInt(res, 10) / this.divider).format(format);
                }
                else {
                    res = '';
                }
                break;
            case 'string':
            case 'number':
            default:
                res = res.toString();
        }
        v.display = res.toString();
        return v;
    }
    render() {
        var _a, _b, _c, _d, _e, _f;
        return h("div", { key: '62e3a468c1b89b5899ab7d5783ca8a9e05760313' }, h("div", { key: '761f65df3db93fa78a6302fbd94b0b0d1e8bea16', class: "heading", innerHTML: DiscoveryPageable.formatLabel(this.data.name) }), !!((_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.tabular) === null || _b === void 0 ? void 0 : _b.onTop) ? this.getPagination() : '', h("table", { key: '8d8876543d34e08de142e4ab413ef91a3a9c88d9', class: "sortable" }, h("thead", { key: '6586ba9a838b072912825e64c08c5ad807cc36ad' }, this.data.headers.map((header, i) => {
            var _a;
            return h("th", { "data-sort": i, class: this.getClasses(i), onClick: () => this.sort(i), style: {
                    width: ((_a = this.options.tabular) === null || _a === void 0 ? void 0 : _a.fixedWidth) ? `${(100 / this.data.headers.length)}%` : 'auto'
                } }, header);
        })), h("thead", { key: 'fae22e293eeabe77877c768e4d698d1c638a4e41' }, ((_d = (_c = this.options) === null || _c === void 0 ? void 0 : _c.tabular) === null || _d === void 0 ? void 0 : _d.filterable) ? this.data.headers.map((header, i) => {
            var _a;
            return h("th", { "data-filter": i, style: {
                    width: ((_a = this.options.tabular) === null || _a === void 0 ? void 0 : _a.fixedWidth) ? `${(100 / this.data.headers.length)}%` : 'auto'
                } }, h("input", { type: "text", class: "discovery-input", onInput: e => this.filter(i, e) }));
        })
            : ''), h("tbody", { key: '5425bf5dd299255fb8b1abd89f5fa88ee8e2ff92' }, this.displayedValues.map((value, i) => h("tr", { class: i % 2 === 0 ? 'odd' : 'even', onClick: () => this.setSelected(value), onMouseOver: () => this.setOver(value), style: this.getRowStyle(i) }, value.map((v, j) => h("td", { style: this.getCellStyle(i, j) }, h("span", { innerHTML: v.display + (v.unit || '') }))))))), !((_f = (_e = this.options) === null || _e === void 0 ? void 0 : _e.tabular) === null || _f === void 0 ? void 0 : _f.onTop) ? this.getPagination() : '');
    }
    getRowStyle(row) {
        const h = this.data.values[row][0];
        const styles = {};
        if (this.data.params && this.data.params[h]) {
            if (!GTSLib.isArray(this.data.params[h])) {
                styles.backgroundColor = this.data.params[h].bgColor;
                styles.color = this.data.params[h].fontColor;
            }
        }
        return styles;
    }
    getCellStyle(row, cell) {
        const h = this.data.values[row][0];
        const styles = {};
        if (this.data.params && this.data.params[h]) {
            if (GTSLib.isArray(this.data.params[h]) && this.data.params[h][cell]) {
                styles.backgroundColor = this.data.params[h][cell].bgColor;
                styles.color = this.data.params[h][cell].fontColor;
            }
        }
        return styles;
    }
    getClasses(i) {
        var _a, _b, _c, _d;
        const c = [];
        if ((_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.tabular) === null || _b === void 0 ? void 0 : _b.sortable) {
            c.push('pointer');
        }
        if (((_d = (_c = this.options) === null || _c === void 0 ? void 0 : _c.tabular) === null || _d === void 0 ? void 0 : _d.sortable) && this.sortCol === i) {
            c.push('sortable');
            if (this.sortAsc) {
                c.push('asc');
            }
            else {
                c.push('desc');
            }
        }
        return c.join(' ');
    }
    getPagination() {
        return h("div", { class: "center" }, h("div", { class: "pagination" }, this.page !== 0 ? h("div", { class: "prev hoverable", onClick: () => this.prev() }, "<") : '', this.page - this.windowed > 0 ? h("div", { class: "index disabled" }, "...") : '', this.pages.length > 1
            ? this.pages.map(c => h("span", null, c >= this.page - this.windowed && c <= this.page + this.windowed
                ? h("span", { class: { index: true, hoverable: this.page !== c, active: this.page === c }, onClick: () => this.goto(c) }, c)
                : '')) : '', this.page + this.windowed < this.pages.length ? h("div", { class: "index disabled" }, "...") : '', this.page + this.elemsCount < (this.data.values || []).length - 1 ?
            h("div", { class: "next hoverable", onClick: () => this.next() }, ">") : ''));
    }
    static get is() { return "discovery-pageable"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["discovery-pageable.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["discovery-pageable.css"]
        };
    }
    static get properties() {
        return {
            "debug": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "debug",
                "reflect": false,
                "defaultValue": "false"
            },
            "divider": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "divider",
                "reflect": false
            },
            "data": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "Dataset",
                    "resolved": "Dataset",
                    "references": {
                        "Dataset": {
                            "location": "import",
                            "path": "../../../model/types",
                            "id": "src/model/types.ts::Dataset"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                }
            },
            "options": {
                "type": "unknown",
                "mutable": true,
                "complexType": {
                    "original": "Param",
                    "resolved": "Param",
                    "references": {
                        "Param": {
                            "location": "import",
                            "path": "../../../model/param",
                            "id": "src/model/param.ts::Param"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "defaultValue": "new Param()"
            },
            "params": {
                "type": "unknown",
                "mutable": true,
                "complexType": {
                    "original": "Param[]",
                    "resolved": "Param[]",
                    "references": {
                        "Param": {
                            "location": "import",
                            "path": "../../../model/param",
                            "id": "src/model/param.ts::Param"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "defaultValue": "[]"
            },
            "elemsCount": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "elems-count",
                "reflect": false,
                "defaultValue": "15"
            },
            "windowed": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "windowed",
                "reflect": false,
                "defaultValue": "5"
            }
        };
    }
    static get events() {
        return [{
                "method": "dataPointOver",
                "name": "dataPointOver",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "dataPointSelected",
                "name": "dataPointSelected",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get watchers() {
        return [{
                "propName": "data",
                "methodName": "updateData"
            }];
    }
}
//# sourceMappingURL=discovery-pageable.js.map
